import { watch } from 'vue'
import useAuthStore, { isUserToken, type TokenList } from '@/stores/authStore'

// Tokens in the session bucket live in sessionStorage, which is per tab and never fires cross-tab
// `storage` events. This channel carries that session between the open tabs of one browser, so a
// login without "remember me" is shared by all of them, dies with the browser and keeps one token
// chain (one `user--authentications` row) across tabs. The persistent bucket is left alone: it
// already syncs through localStorage `storage` events, and applying a remote snapshot with
// `storage: 'session'` would empty that shared bucket for every tab.
//
// The setup is module-level and memoized: the router's auth guard may run before App.vue's setup
// (vue-router starts the initial navigation on install), and it needs the channel alive to receive
// the session from a peer tab. Whichever of useAuthTabSync()/waitForPeerSessionHydration() runs
// first initializes; watchers outside a component are never auto-stopped, which is exactly right
// for app-lifetime sync.

interface SessionSnapshot {
  /** The `token_id` claim of the selected token: every tab sharing one login carries the same chain. */
  chain: string
  tokens: TokenList
  selectedToken: number
  sessionEnd?: number
}

type AuthMessage =
  | ({ type: 'sync' } & SessionSnapshot)
  | { type: 'logout'; chain: string }
  | { type: 'request-session' }

const CHANNEL_NAME = 'ada-app-ui-auth'
// BroadcastChannel latency is ~1-5 ms; 250 ms is a generous ceiling only ever
// paid (once) by a genuinely logged-out user opening the app.
const HYDRATION_TIMEOUT_MS = 250

/**
 * Settles once the startup handshake is done: `true` as soon as a peer tab's
 * session has been applied, `false` when no peer answered within the timeout.
 */
let hydration: Promise<boolean> | null = null

function chainOf(payload: unknown): string {
  return isUserToken(payload) ? payload.token_id : ''
}

function init(): Promise<boolean> {
  if (hydration != null) return hydration

  const authStore = useAuthStore()
  const channel = new BroadcastChannel(CHANNEL_NAME)

  const inSessionMode = () => authStore.authPersistence === 'session'
  const hasPersistentTokens = () => !inSessionMode() && authStore.tokensList.length > 0

  // Only the session bucket is ever broadcast; in persistent mode the snapshot is empty.
  function snapshot(): SessionSnapshot {
    const tokens = inSessionMode() ? authStore.tokensList : []
    return {
      chain: chainOf(tokens.find((entry) => entry.selected)?.payload),
      tokens: tokens.map(({ token }) => ({ token })),
      selectedToken: tokens.findIndex((entry) => entry.selected),
      sessionEnd: authStore.sessionEnd,
    }
  }

  // Echo guard: a snapshot we just applied from a peer must not be rebroadcast.
  // We compare serialized state (robust against Vue's async watcher flush) rather
  // than a time-based flag.
  let lastSignal = ''
  // Chain of the last non-empty session snapshot: tags the logout so that tabs on a newer chain ignore it.
  let lastChain = snapshot().chain

  function applyRemote(msg: SessionSnapshot) {
    authStore.setTokenList(msg.tokens, {
      storage: 'session',
      sessionTimeoutMinutes: msg.sessionEnd != null ? (msg.sessionEnd - Date.now()) / 60_000 : undefined,
    })
    // Out-of-range selectedToken (shouldn't happen) → index 0; sync converges via one extra sync.
    authStore.selectToken(authStore.tokensList[msg.selectedToken]?.token)
    // Read back after mutating: the watcher flushes later, sees the same signature and stays silent.
    const applied = snapshot()
    lastSignal = JSON.stringify(applied)
    lastChain = applied.chain
  }

  // Local session changes → broadcast to peers.
  watch(
    () => JSON.stringify(snapshot()),
    (sig) => {
      if (sig === lastSignal) return // came from a peer — don't echo back
      const state = snapshot()
      if (state.tokens.length > 0) {
        lastChain = state.chain
        channel.postMessage({ type: 'sync', ...state })
      } else if (!hasPersistentTokens()) {
        // The session bucket emptied and the tokens did not move to the persistent bucket: a logout,
        // a rejected refresh or an expired session — not a "remember me" login made in this tab.
        channel.postMessage({ type: 'logout', chain: lastChain })
      }
    }
  )

  channel.onmessage = (event: MessageEvent<AuthMessage>) => {
    const msg = event.data
    if (msg.type === 'sync') {
      // A remembered (persistent) login is never overwritten or moved into the session bucket.
      if (!hasPersistentTokens()) applyRemote(msg)
    } else if (msg.type === 'logout') {
      // Only the tab's own chain may log it out: a tab whose chain the server rejected
      // ("User is logged on elsewhere") must not kill a newer login.
      if (!inSessionMode() || msg.chain !== snapshot().chain) return
      authStore.clearAllTokens()
      lastSignal = JSON.stringify(snapshot())
    } else if (inSessionMode() && authStore.tokensList.length > 0) {
      // 'request-session': a peer started without a session and is asking — reply if we have one.
      channel.postMessage({ type: 'sync', ...snapshot() })
    }
  }

  const hasTokens = () => authStore.tokensList.length > 0

  if (hasTokens()) {
    hydration = Promise.resolve(true)
  } else {
    // Hydration on startup: ask already-open tabs for their session, and let
    // callers (the router guards, the login page) hold their decision until
    // a peer answers or the timeout passes.
    channel.postMessage({ type: 'request-session' })
    hydration = new Promise<boolean>((resolve) => {
      const stop = watch(hasTokens, (authed) => {
        if (authed) {
          stop()
          resolve(true)
        }
      })
      setTimeout(() => {
        stop()
        resolve(false)
      }, HYDRATION_TIMEOUT_MS)
    })
  }

  return hydration
}

export function useAuthTabSync(): void {
  // Older Safari (<15.4) lacks BroadcastChannel — degrade silently to a per-tab
  // session (still sessionStorage, just no cross-tab sync) rather than crash.
  // Same graceful-degradation pattern useAuth.refreshToken uses for navigator.locks.
  if (typeof BroadcastChannel === 'undefined') return
  void init()
}

/**
 * Router guard and login page helper: a freshly opened tab may be about to
 * receive the session from an open peer tab. Await this before deciding to
 * redirect to /login, before honouring a `user_id` switch in the query, or
 * before starting a new login, so deep links opened in a new tab neither
 * bounce to the login page, continue as the wrong user, nor start a second login.
 * Memoized — only the first caller ever waits. The value is a wait, never an
 * auth pass: decide from live store state afterwards.
 */
export async function waitForPeerSessionHydration(): Promise<boolean> {
  if (typeof BroadcastChannel === 'undefined') return false
  return init()
}
